/* jshint browser:true */
import Glide from '@glidejs/glide';
import DomUtils from '../dom-utils';

/**
 * Management for carousels/sliders.
 */
export default class Carousel {
  /**
   * Constructor.
   */
  constructor() {
    this.options = {
      overlayCaptionAtWidth: 1024,
    };

    this.icons = {
      arrowLeft:
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 256 512" class="r-button__media" aria-hidden="true" role="img"><path d="M231.293 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L70.393 256 251.092 74.87c4.686-4.686 4.686-12.284 0-16.971L231.293 38.1c-4.686-4.686-12.284-4.686-16.971 0L4.908 247.515c-4.686 4.686-4.686 12.284 0 16.971L214.322 473.9c4.687 4.686 12.285 4.686 16.971-.001z"></path></svg>',
      arrowRight:
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 256 512" class="r-button__media" aria-hidden="true" role="img"><path d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.13c-4.686 4.686-4.686 12.284 0 16.971L24.707 473.9c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.687-12.285-4.687-16.971 0z"></path></svg>',
    };

    this.classNames = {
      track: 'hero-carousel__track',
      slides: 'hero-carousel__slides',
      slideInfo: 'hero-carousel__info',
      mediaWrapper: 'hero-carousel__media',
      captionWrapper: 'hero-carousel__content',
      curSlideNum: 'hero-carousel__cur-slide-num',
      arrows: 'hero-carousel__arrows',
      arrowPrev:
        'hero-carousel__arrow-prev r-button r-button-icon r-button-icon--full',
      arrowNext:
        'hero-carousel__arrow-next r-button r-button-icon r-button-icon--full',
      jsReady: 'js-hero-carousel--ready',
    };

    this.modifierClassNames = {
      randomOrder: 'hero-carousel--random-order',
      randomSlide: 'hero-carousel--random-slide',
    };
  }

  /**
   * Initializes carousels.
   * @param {string} selector CSS selector to find carousels. The selected
   *        elements should conform to the DOM structure specified by
   *        [Glide.js](https://glidejs.com/).
   */
  init(selector) {
    const wrappers = document.querySelectorAll(selector);

    if (!wrappers) {
      throw new ReferenceError(`No carousels found for selector: ${selector}`);
    }

    wrappers.forEach((x) => this.setup(x));

    // Trigger recalculation after setup
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  /**
   * Sets up an individual carousel.
   * @param {Element} wrapper The root element of a carousel.
   */
  async setup(wrapper) {
    const slides = wrapper.querySelector('.' + this.classNames.slides);
    const slideCount = slides.children.length;

    // Only implement if more than one slide
    if (slideCount < 2) {
      this.markReady(wrapper);
      return;
    }

    // Choose a slide at random if enabled
    const isRandomSlide = wrapper.classList.contains(
        this.modifierClassNames.randomSlide,
    );

    if (isRandomSlide) {
      this.chooseRandomSlide(slides);
      this.markReady(wrapper);
      return;
    }

    // Randomize slide order if enabled
    const isRandomOrder = wrapper.classList.contains(
        this.modifierClassNames.randomOrder,
    );

    if (isRandomOrder) {
      this.randomizeSlides(slides);
    }

    // Add controls
    const slideInfo = this.addSlideInfo(wrapper, slideCount);

    this.addArrows(wrapper);

    // Add data attributes
    const track = wrapper.querySelector('.' + this.classNames.track);

    track.dataset.glideEl = 'track';

    // Start Glide
    const glide = new Glide(wrapper);

    glide.on('run.after', () => {
      this.updateSlideInfo(slideInfo, glide.index);
    });

    glide.on('build.after', () => {
      this.markReady(wrapper);
    });

    DomUtils.observeResize(wrapper, () => {
      // very much violates encapsulation
      // We should probably push ResizeObserver support upstream
      try {
        glide._e.emit('resize');
      } catch (e) {
        // If glide changes its internals, just do nothing
      }
    });

    glide.mount();
  }

  /**
   * Adds a class to the wrapper element to mark it as being ready.
   * @param {Element} wrapper The root element of a carousel.
   */
  markReady(wrapper) {
    wrapper.classList.add(this.classNames.jsReady);
  }

  /**
   * Chooses one slide at random, then removes the others.
   * @param {Element} slideWrapper An element that wraps a carousel's slides.
   */
  chooseRandomSlide(slideWrapper) {
    const slides = Array.from(slideWrapper.children);
    const slideCount = slides.length;
    const randomIndex = Math.floor(Math.random() * slideCount);

    slides.forEach((slide, curIndex) => {
      if (curIndex !== randomIndex) {
        slideWrapper.removeChild(slide);
      }
    });
  }

  /**
   * Changes the DOM order of a carousel's slides to a random order.
   * @param {Element} slideWrapper An element that wraps a carousel's slides.
   */
  randomizeSlides(slideWrapper) {
    const slides = Array.from(slideWrapper.children);
    const slideCount = slides.length;
    let curIndex = slideCount;

    // Detach slides from DOM
    slides.forEach((x) => slideWrapper.removeChild(x));

    // Shuffle slides
    while (curIndex !== 0) {
      const randomIndex = Math.floor(Math.random() * curIndex);
      const tempSlide = slides[--curIndex];

      slides[curIndex] = slides[randomIndex];
      slides[randomIndex] = tempSlide;
    }

    // Reattach slides to DOM
    slides.forEach((x) => slideWrapper.appendChild(x));
  }

  /**
   * Creates an area to display the current slide number.
   * @param {Element} wrapper The root element of a carousel.
   * @param {number} slideCount The number of slides.
   *
   * @return {HTMLDivElement} The element with the slide info.
   */
  addSlideInfo(wrapper, slideCount) {
    const slideInfo = document.createElement('div');

    // Set up slide info
    slideInfo.className = this.classNames.slideInfo;
    slideInfo.setAttribute('aria-live', 'polite');
    slideInfo.setAttribute('aria-atomic', 'true');
    slideInfo.innerHTML =
      `Slide ` +
      `<span class="${this.classNames.curSlideNum}">1</span> of ${slideCount}`;

    // Add element to DOM
    wrapper.appendChild(slideInfo);

    return slideInfo;
  }

  /**
   * Creates buttons to act as prev/next arrows.
   * @param {Element} wrapper The root element of a carousel.
   */
  addArrows(wrapper) {
    const arrows = document.createElement("div");
    const buttonPrev = document.createElement("button");
    const buttonNext = document.createElement("button");

    // Set up button wrapper
    arrows.className = this.classNames.arrows;
    arrows.dataset.glideEl = "controls";

    // Set up buttons
    buttonPrev.className = this.classNames.arrowPrev;
    buttonPrev.type = "button";
    buttonPrev.title = "See previous slide";
    buttonPrev.innerHTML = this.icons.arrowLeft;
    buttonPrev.dataset.glideDir = "<";

    buttonNext.className = this.classNames.arrowNext;
    buttonNext.type = "button";
    buttonNext.title = "See next slide";
    buttonNext.innerHTML = this.icons.arrowRight;
    buttonNext.dataset.glideDir = ">";

    // Add elements to DOM
    arrows.appendChild(buttonPrev);
    arrows.appendChild(buttonNext);
    wrapper.appendChild(arrows);

    return arrows;
  }

  /**
   * Updates the slide info area.
   * @param {Element} slideInfo The slide info area to update.
   * @param {number} curSlideIndex The current slide's index (zero-based).
   */
  updateSlideInfo(slideInfo, curSlideIndex) {
    const curSlideWrapper = slideInfo.querySelector(
      "." + this.classNames.curSlideNum
    );

    curSlideWrapper.textContent = (curSlideIndex + 1).toString();
  }
}
